import type { Expression } from 'mapbox-gl';
import type { GeoJsonProperties, Polygon } from 'geojson';

import type { SourceRow } from '../../types';
import type {
  BoundingBox,
  FacetBucket,
  MapFilterMode,
  MapQueryFilterParams,
  MapQueryFilters,
  MapQueryResultRow,
  MapQuerySearchParams,
  MapQuerySpatial,
  MapQueryTicks,
} from './types';

/**
 * Ticked entities take these colours in tick order. They avoid the result blue
 * and the focus orange, so an entity's colour never reads as a map state.
 */
export const ENTITY_COLORS = [
  '#6929c4',
  '#009d9a',
  '#ee538b',
  '#b28600',
  '#9f1853',
  '#198038',
  '#fa4d56',
  '#005d5d',
  '#a56eff',
  '#570408',
];

/** Every Tract no ticked entity owns. */
export const NEUTRAL_COLOR = '#697077';
/** The outline of a Tract in the results. */
export const RESULT_COLOR = '#0072ed';
/** The outline of the focused Tract. */
export const FOCUS_COLOR = '#ff6b00';
/** The dashed outline of the visible extent being searched. */
export const EXTENT_COLOR = '#0f62fe';

/**
 * The colour of the entity ticked `index`-th. The palette repeats past its end.
 *
 * @param {number} index - The entity's place in tick order
 * @return {string} Its colour
 */
export const entityColor = (index: number): string =>
  ENTITY_COLORS[index % ENTITY_COLORS.length];

/**
 * The search's map area for the Map Filter section's state. An AOI counts only
 * once its ring is closed (a drawn polygon), and the visible extent once the map
 * has reported it.
 *
 * @param {MapFilterMode} mode - The Map Filter radio
 * @param {BoundingBox | null} extent - The last visible extent
 * @param {Polygon | null} polygon - The drawn polygon
 * @return {MapQuerySpatial} The `spatial` the search takes
 */
export const spatialParam = (
  mode: MapFilterMode,
  extent: BoundingBox | null,
  polygon: Polygon | null
): MapQuerySpatial => {
  if (mode === 'extent' && extent) return { boundingBox: extent };
  if (mode === 'aoi' && polygon) return { polygonGeojson: polygon };
  return null;
};

/** No location ticked, No Map Filter, no extent and no polygon: a fresh Map Query. */
export const EMPTY_MAP_QUERY_FILTERS: MapQueryFilters = {
  locations: [],
  mode: 'none',
  extent: null,
  polygon: null,
};

/**
 * The filters a Map Query draft is built from: the ticked entities, the ticked
 * location groups and the map area. Ids and groups are sorted, as the server
 * stores them, so ticks undone and redone compare equal to what was sent.
 *
 * @param {SourceRow[]} entities - The ticked entities
 * @param {MapQueryFilters} filters - The rest of the filters
 * @return {MapQueryFilterParams} The draft's `mapQuery`
 */
export const mapQueryFilterParams = (
  entities: SourceRow[],
  { locations, mode, extent, polygon }: MapQueryFilters
): MapQueryFilterParams => ({
  entityIds: entities.map(({ id }) => id).sort((a, b) => a - b),
  locationGroups: locations.map(({ id }) => id).sort(),
  spatial: spatialParam(mode, extent, polygon),
});

/**
 * What decides which assets a search returns, as one string: everything but the
 * page, the sort and the entity name search.
 *
 * @param {MapQuerySearchParams} params - A search, or its filters
 * @return {string} Its results' key
 */
export const resultsKey = ({
  asOfDate,
  entityIds,
  locationGroups,
  spatial,
  listedTractIds,
}: Pick<
  MapQuerySearchParams,
  'asOfDate' | 'entityIds' | 'locationGroups' | 'spatial' | 'listedTractIds'
>): string =>
  JSON.stringify([
    asOfDate ?? null,
    entityIds,
    locationGroups,
    spatial,
    listedTractIds ?? [],
  ]);

/**
 * A result row's id, unique across asset types: a Tract's Salesforce id (the
 * tract tiles' `tract_id`), else its kind and asset id, `"wellbore-12"`.
 *
 * @param {MapQueryResultRow} row - The row
 * @return {string} Its id
 */
export const resultRowId = (row: MapQueryResultRow): string =>
  row.kind === 'tract' ? row.salesforceId : `${row.kind}-${row.id}`;

/** A wellbore or LTD feature's {@link resultRowId}, as a map expression. */
export const NON_TRACT_ROW_ID: Expression = [
  'concat',
  ['get', 'kind'],
  '-',
  ['to-string', ['get', 'asset_id']],
];

/**
 * The {@link resultRowId} of a result drawn on the map: a Tract's (`tract_id`,
 * from the tiles or the listed shapes) or a wellbore or LTD asset's.
 *
 * @param {GeoJsonProperties} properties - The feature's properties
 * @return {string | null} Its row id, null for a feature of neither
 */
export const resultFeatureId = (
  properties: GeoJsonProperties
): string | null => {
  if (typeof properties?.tract_id === 'string') return properties.tract_id;
  const { kind, asset_id: assetId } = properties ?? {};
  return typeof kind === 'string' &&
    (typeof assetId === 'number' || typeof assetId === 'string')
    ? `${kind}-${assetId}`
    : null;
};

/**
 * Which rows a search answers with: all of it but the entity text, which
 * narrows only the entity options.
 *
 * @param {MapQuerySearchParams} search - The search
 * @return {string} A key equal for two searches with the same rows
 */
export const rowsKey = (search: MapQuerySearchParams): string =>
  JSON.stringify({ ...search, entityText: undefined });

/**
 * The corners of the visible extent, from the rectangle `QueryMap` reports for
 * it (west-south first, then east-south, east-north).
 *
 * @param {Polygon} viewport - The reported viewport rectangle
 * @return {BoundingBox} Its corners
 */
export const boundingBoxOf = (viewport: Polygon): BoundingBox => {
  const [[west, south], , [east, north]] = viewport.coordinates[0];
  return { swLng: west, swLat: south, neLng: east, neLat: north };
};

/**
 * A box drawn a little inside `box`, so its outline shows inside the viewport it
 * was captured from.
 *
 * @param {BoundingBox} box - The visible extent
 * @param {number} fraction - How far in each side moves, as a share of the box
 * @return {Polygon} The inset rectangle
 */
export const insetRectangle = (
  { swLng, swLat, neLng, neLat }: BoundingBox,
  fraction: number
): Polygon => {
  const dx = (neLng - swLng) * fraction;
  const dy = (neLat - swLat) * fraction;
  const [west, south, east, north] = [
    swLng + dx,
    swLat + dy,
    neLng - dx,
    neLat - dy,
  ];
  return {
    type: 'Polygon',
    coordinates: [
      [
        [west, south],
        [east, south],
        [east, north],
        [west, north],
        [west, south],
      ],
    ],
  };
};

/**
 * A polygon as unpadded base64url GeoJSON, as the tract tiles take it.
 *
 * @param {Polygon} polygon - The drawn polygon
 * @return {string} The encoded polygon
 */
const base64UrlJson = (polygon: Polygon): string =>
  btoa(JSON.stringify(polygon))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

/** The search's filters, as the tract tiles take them. */
export interface TractTileFilters {
  asOfDate: string | null;
  entityIds: number[];
  locationGroups: string[];
  /** The drawn polygon, under Draw AOI Polygon. */
  polygon: Polygon | null;
  /** The visible extent searched, under Use Visible Map Extent. */
  extent: BoundingBox | null;
}

/**
 * The tract tile URL: the search's filters, so the tiles hold only its matches
 * and a change of filters loads new ones.
 *
 * @param {string} template - The tracts source's tile URL template
 * @param {TractTileFilters} filters - The search's filters
 * @return {string} The URL template
 */
export const tractTilesUrl = (
  template: string,
  { asOfDate, entityIds, locationGroups, polygon, extent }: TractTileFilters
): string => {
  const params = new URLSearchParams();
  if (asOfDate) params.append('as_of_date', asOfDate);
  entityIds.forEach((id) => params.append('entity_ids[]', String(id)));
  locationGroups.forEach((id) => params.append('location_groups[]', id));
  if (polygon) params.append('poly_geojson', base64UrlJson(polygon));
  if (extent) {
    const { swLng, swLat, neLng, neLat } = extent;
    params.append('bbox', [swLng, swLat, neLng, neLat].join(','));
  }
  const query = params.toString();
  return `${template}${query ? `?${query}` : ''}`;
};

/**
 * Whether the ticks narrow the matches below every Tract (or every one in the
 * visible extent): an entity, a location or a drawn polygon. Narrowed, they are
 * drawn at every zoom, as the package pages draw theirs; otherwise from the
 * tracts source's minzoom (county scale).
 *
 * @param {MapQueryTicks} ticks - The current ticks
 * @return {boolean} True when narrowed
 */
export const narrowsResults = ({
  entityIds,
  locationGroups,
  polygonDrawn,
}: MapQueryTicks): boolean =>
  entityIds.length > 0 || locationGroups.length > 0 || polygonDrawn;

/** A facet option as a section lists it. */
export interface FacetOption<TId extends string | number> {
  id: TId;
  name: string;
  count: number;
}

/**
 * A facet's options: the search's buckets (only options with a count above 0,
 * in the server's order), then each ticked option the buckets no longer hold, at
 * 0, so a tick never drops out of the list. A bucket with no name (an entity
 * record since hard-deleted) has nothing to show, so it is left out.
 *
 * @param {FacetBucket[]} buckets - The search's buckets
 * @param {FacetOption[]} ticked - The ticked options, named as when ticked
 * @return {FacetOption[]} The options to list
 */
export const facetOptions = <TId extends string | number>(
  buckets: FacetBucket<TId>[],
  ticked: readonly { id: TId; name: string }[]
): FacetOption<TId>[] => {
  const named = buckets.flatMap((bucket) =>
    bucket.name === null ? [] : [{ ...bucket, name: bucket.name }]
  );
  const listed = new Set(named.map((option) => option.id));
  return [
    ...named,
    ...ticked
      .filter((option) => !listed.has(option.id))
      .map((option) => ({ ...option, count: 0 })),
  ];
};

/**
 * The options whose name contains `text`, ignoring case. Blank text keeps all.
 *
 * @param {FacetOption[]} options - The options
 * @param {string} text - The search box's text
 * @return {FacetOption[]} The matching options
 */
export const optionsMatching = <TId extends string | number>(
  options: FacetOption<TId>[],
  text: string
): FacetOption<TId>[] => {
  const needle = text.trim().toLowerCase();
  if (needle === '') return options;
  return options.filter((option) => option.name.toLowerCase().includes(needle));
};

// ─────────────────────────────────────
// Map style. The tiles hold only the Tracts the search matches, each with its
// owners; the map paints each in its first ticked owner's colour.
// ─────────────────────────────────────

const OWNERS: Expression = ['to-string', ['get', 'entity_ids']];

/**
 * Whether a Tract's owners include the entity: the id pipe-wrapped, so 12 never
 * matches 1201.
 *
 * @param {number} entityId - The entity
 * @return {Expression} The test
 */
const ownedBy = (entityId: number): Expression => [
  'in',
  `|${entityId}|`,
  OWNERS,
];

/**
 * A Tract's entity colour: the first ticked entity among its owners, else the
 * neutral colour (no entity ticked, or a Tract held only through a deal).
 *
 * @param {number[]} entityIds - Ticked entities, in tick order
 * @return {Expression | string} The colour expression
 */
export const entityColorExpression = (
  entityIds: number[]
): Expression | string =>
  entityIds.length === 0
    ? NEUTRAL_COLOR
    : [
        'case',
        ...entityIds.flatMap((id, index) => [ownedBy(id), entityColor(index)]),
        NEUTRAL_COLOR,
      ];

/** The tract layer's paint. */
export interface TractPaint {
  fillColor: Expression | string;
  fillOpacity: number;
  lineColor: string;
  lineWidth: Expression;
}

/**
 * The tract layer's paint: every Tract drawn is a match, filled in its entity
 * colour and outlined in the result blue, the outline thinning out below the
 * tracts source's minzoom (county scale), where a Tract is a few pixels and a
 * full one would hide its colour.
 *
 * @param {number[]} entityIds - Ticked entities, in tick order
 * @param {number} tractMinZoom - The tracts source's minzoom
 * @return {TractPaint} Fill and outline paint
 */
export const tractPaint = (
  entityIds: number[],
  tractMinZoom: number
): TractPaint => ({
  fillColor: entityColorExpression(entityIds),
  fillOpacity: 0.6,
  lineColor: RESULT_COLOR,
  lineWidth: ['interpolate', ['linear'], ['zoom'], 5, 0.5, tractMinZoom, 2.5],
});
